<template>
  <div :class="previewClass">
    <div v-if="isOpened" class="preview__container">
      <img
        v-if="isImage && sourceIsReady"
        class="preview__image"
        v-lazy="srcMultimedia"
      />

      <video-player
        v-if="isVideo"
        class="preview__video"
        :options="videoOptions"
      />

      <map-static
        v-if="isLocation"
        :lat="coordinates.lat"
        :lng="coordinates.lng"
        :classes="'preview__image'"
      />

      <hcc-audio
        v-if="isAudio && sourceIsReady"
        :src="srcMultimedia"
        :ext="audioExtension"
      />

      <div v-if="isDocument" class="preview__document">
        <document-icon class="preview__icon" />
        <span class="preview__label">{{ filename }}</span>
        <span class="preview__label preview__label--muted">{{
          docSize | filesize
        }}</span>
      </div>
      <span v-if="notExist" class="preview__label">{{
        $t("chat.data-bank-error")
      }}</span>
    </div>

    <div class="preview__actions">
      <hcc-button
        @click="cancel"
        variant="transparent"
        color="alert"
        class="preview__button"
      >
        {{ $t("shared-components.confirmation.cancel") }}
      </hcc-button>

      <hcc-button
        :disabled="notExist"
        @click="confirm"
        variant="fill"
        color="secondary"
        class="preview__button"
      >
        {{ $t("chat.send") }}
      </hcc-button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import DocumentIcon from '@/assets/icons/document.svg';

export default {
  components: {
    HccAudio: () => import('@/components/shared/HccAudio/index.vue'),
    HccButton: () => import('@/components/shared/HccButton/index.vue'),
    MapStatic: () => import('@/components/shared/MapStaticLocation.vue'),
    VideoPlayer: () => import('@/components/shared/VideoPlayer.vue'),
    DocumentIcon,
  },
  props: {
    message: {
      type: Object,
    },
  },
  data() {
    return {
      notExist: false,
      srcMultimedia: '',
      docName: '',
      docSize: 0,
      sourceIsReady: false,
    };
  },
  watch: {
    message(val) {
      if (val !== null) {
        this.triggerMediaLoad();
      } else {
        this.srcMultimedia = '';
        this.docName = '';
        this.docSize = 0;
        this.sourceIsReady = false;
      }
      this.notExist = false;
    },
  },
  computed: {
    ...mapState(['token']),
    tokenQuery() {
      return `?token=${this.token}`;
    },
    isOpened() {
      return !!this.message;
    },
    previewClass() {
      return [
        'preview',
        {
          'is-opened': this.isOpened,
        },
      ];
    },
    fileMultimedia() {
      return this.message && this.message.multimedia ? this.message.multimedia : null;
    },
    fileLink() {
      return this.message && this.message.link ? `${this.message.link}${this.tokenQuery}` : null;
    },
    isImage() {
      return this.message ? this.message.type === 'image' : false;
    },
    isMultimediaImage() {
      return this.isImage && !!this.fileMultimedia;
    },
    isUrlImage() {
      return this.isImage && !!this.fileLink;
    },
    isVideo() {
      return this.message ? this.message.type === 'video' : false;
    },
    videoOptions() {
      return {
        autoplay: false,
        controls: true,
        loop: false,
        sources: [{
          src: this.fileLink,
          type: 'video/mp4',
        }],
        controlBar: {
          pictureInPictureToggle: false,
        },
      };
    },
    isLocation() {
      return this.message ? this.message.type === 'location' : false;
    },
    coordinates() {
      return this.message && this.message.location ? this.message.location : null;
    },
    isAudio() {
      return this.message ? this.message.type === 'audio' : false;
    },
    audioExtension() {
      if (this.isAudio) {
        const filename = this.fileMultimedia.name;
        return filename.substring(filename.lastIndexOf('.') + 1);
      }

      return '';
    },
    isDocument() {
      return this.message ? this.message.type === 'document' : false;
    },
    isMultimediaDocument() {
      return this.isDocument && !!this.fileMultimedia;
    },
    isUrlDocument() {
      return this.isDocument && !!this.fileLink;
    },
    filename() {
      return this.isDocument ? this.name : '';
    },
    name() {
      return this.message.filename || this.fileMultimedia.name || 'File';
    },
  },
  created() {
    document.addEventListener('keyup', (event) => {
      if (this.isOpened) {
        const { code } = event;

        switch (code) {
          case 'Escape':
            this.cancel();
            break;
          case 'Enter':
            this.confirm();
            break;
          default:
        }
      }
    });
  },
  destroyed() {
    document.removeEventListener('keyup', null);
  },
  methods: {
    confirm() {
      this.$emit('confirm');
    },
    cancel() {
      this.$emit('cancel', this.notExist);
    },
    triggerMediaLoad() {
      if (this.isMultimediaImage || this.isAudio) {
        this.readFileSource();
      }

      if (this.isUrlImage) {
        this.srcMultimedia = this.fileLink;
        this.sourceIsReady = true;
      }

      if (this.isMultimediaDocument) {
        this.docName = this.fileMultimedia.name ?? 'filename';
        this.docSize = this.fileMultimedia.size ?? 0;
      }

      if (this.fileLink) {
        this.readDocumentPreview();
      }
    },
    readFileSource() {
      const reader = new FileReader();

      reader.onload = (e) => {
        this.srcMultimedia = e.target.result;
        this.sourceIsReady = true;
      };

      reader.readAsDataURL(this.fileMultimedia);
    },
    readDocumentPreview() {
      fetch(this.fileLink)
        .then((res) => {
          if (res.status === 404) {
            this.notExist = true;
          }
          return res.blob();
        })
        .then((blob) => {
          this.docSize = blob.size ?? 0;
        });
    },
  },
};
</script>
